/**
 * ChatPromoteToWorkStream — chat→WorkStream promotion affordance (Story 3).
 *
 * Renders a MarthaChip-style affordance ("Turn this into a WorkStream →") when
 * a TopicPromotionSuggestion is available for the active chat thread. Tapping
 * it opens the WorkStreamCreateWizard with `suggested_name` and
 * `detected_client` pre-filled, and the trigger defaulted to "From-Chat".
 *
 * The component guards on suggestion presence: no suggestion → renders
 * nothing, no failed calls, no console errors (Story 3 AC-3).
 *
 * Fallback path (Story 3 AC-5): the martha-v5 backend does not yet expose a
 * `GET /conversations/:id/topic-suggestions` endpoint. Callers may pass a
 * `fallbackSuggestedName` (e.g. the chat thread title) which the component
 * wraps into a TopicPromotionSuggestion so the affordance still renders and the
 * wizard still pre-fills. The frontend contract is unchanged either way — it
 * consumes TopicPromotionSuggestion. The endpoint absence is flagged as an
 * assumption to confirm at build time (see card spec §6).
 */

import { useState, useCallback, useMemo } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { WorkStreamCreateWizard } from '../../panes/archie/pages/workstreams/WorkStreamCreateWizard';
import type { TopicPromotionSuggestion } from '../../panes/archie/pages/workstreams/types';

export interface ChatPromoteToWorkStreamProps {
  /**
   * Suggestion emitted by the Topics pipeline for the active thread. When
   * absent, the component falls back to `fallbackSuggestedName` (if any) and
   * renders nothing otherwise.
   */
  suggestion?: TopicPromotionSuggestion | null;
  /** chat thread title — used as suggested_name when no backend suggestion */
  fallbackSuggestedName?: string | null;
  /** detected client for the active thread, if known client-side */
  detectedClient?: { id: string; name: string } | null;
  /**
   * Called with the create payload when the wizard is submitted. Caller posts
   * to /workstreams and refreshes state. If omitted, the wizard's create is a
   * no-op (useful when hosted somewhere the create is handled elsewhere).
   */
  onCreate?: (payload: import('../../panes/archie/pages/workstreams/types').CreateWorkStreamPayload) => void;
  className?: string;
}

export function ChatPromoteToWorkStream({
  suggestion,
  fallbackSuggestedName,
  detectedClient,
  onCreate,
  className,
}: ChatPromoteToWorkStreamProps) {
  const [open, setOpen] = useState(false);

  // Resolve the effective suggestion — real pipeline suggestion, or a
  // client-side fallback built from the thread title (Story 3 AC-5).
  const effectiveSuggestion = useMemo<TopicPromotionSuggestion | null>(() => {
    if (suggestion) return suggestion;
    const fallbackName = fallbackSuggestedName?.trim();
    if (!fallbackName) return null;
    return {
      type: 'topic_promotion_suggestion',
      suggested_name: fallbackName,
      suggested_type: 'general',
      detected_client: detectedClient ?? undefined,
    };
  }, [suggestion, fallbackSuggestedName, detectedClient]);

  const handleCreate = useCallback(
    (payload: import('../../panes/archie/pages/workstreams/types').CreateWorkStreamPayload) => {
      onCreate?.(payload);
      setOpen(false);
    },
    [onCreate],
  );

  // Guard on suggestion presence — render nothing when there's nothing to
  // promote (Story 3 AC-3: no failed calls, no console errors).
  if (!effectiveSuggestion) return null;

  return (
    <div className={className}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-primary-300 bg-primary-50 text-primary-700 text-xs font-medium hover:bg-primary-100 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary-400"
        aria-label="Turn this conversation into a WorkStream"
      >
        Turn this into a WorkStream
        <ArrowUpRight size={12} />
      </button>
      <WorkStreamCreateWizard
        open={open}
        onClose={() => setOpen(false)}
        onCreate={handleCreate}
        initialValues={{ suggestion: effectiveSuggestion }}
      />
    </div>
  );
}
